import { useId, useMemo, useState, type ReactNode } from 'react';
import { Button, Chip, SearchField } from '@heroui/react';
import { EmptyState } from '@heroui-pro/react/empty-state';
import { ChevronDown, CircleCheck, Inbox, Plus, Presentation, SearchX, TriangleAlert } from 'lucide-react';
import { ManualCaseSheet } from './ManualCaseSheet';
import { BLOCKS, blockOfAlert, buildTable, isMaxPriority, type BoardAlert } from '@odd/domain';
import { useBoardCtx } from './BoardContext';
import { useErpLinks } from '../lib/erp';
import { CountChip, ErpLink, LightChip, Meter, Panel, SegmentBadge, valueText } from './ui';
import { makeMatcher } from '../lib/search';
import './Table.css';

const SEARCH_PAGE_SIZE = 100;

/** Fila de un caso (se usa en la mesa y en el espacio de cada tándem). `trailing`: acción extra a la derecha. */
export function CaseRow({ a, trailing }: { a: BoardAlert; trailing?: ReactNode }) {
  const { rules, openCase, board } = useBoardCtx();
  const erp = useErpLinks();
  const rule = rules[a.ruleId];
  return (
    <li className="case-row flex items-center gap-1">
      <button
        type="button"
        onClick={() => openCase(a)}
        className="case-row-button group grid min-w-0 flex-1 grid-cols-[auto_1fr_auto] items-center gap-x-4 gap-y-1 rounded-xl px-3 py-3 text-left transition-colors hover:bg-card-2 sm:grid-cols-[auto_1fr_10rem]"
      >
        <SegmentBadge segment={a.segment} />
        <div className="min-w-0">
          <div className="case-row-heading flex flex-wrap items-center gap-2">
            <span className="case-row-title truncate font-bold text-ink">{a.caseTitle}</span>
            {a.manual && (
              <Chip size="sm" variant="soft" color="warning">
                <Chip.Label>Manual</Chip.Label>
              </Chip>
            )}
            {isMaxPriority(a, board.scoring) && (
              <Chip size="sm" variant="soft" color="accent">
                <Chip.Label>Prioridad máx.</Chip.Label>
              </Chip>
            )}
            {a.broughtByTandem && (
              <Chip size="sm" variant="soft" color="success">
                <CircleCheck className="size-3" />
                <Chip.Label>Tándem</Chip.Label>
              </Chip>
            )}
          </div>
          <div className="case-row-meta mt-0.5 truncate text-sm text-ink-3">
            {a.communityName} · {a.affName ?? 'Sin AFF'}
            {a.daysToMeeting != null && <> · junta en {a.daysToMeeting} d</>}
          </div>
          {a.related && a.related.length > 0 && (
            <div className="mt-1 flex flex-wrap gap-1">
              {a.related.map((r) => (
                <span key={r.key} className="rounded-full bg-card-2 px-2 py-0.5 text-xs text-ink-2">
                  también {rules[r.ruleId]?.name.toLowerCase() ?? r.ruleId} ·{' '}
                  {valueText(rules[r.ruleId], r.value)}
                </span>
              ))}
            </div>
          )}
        </div>
        <div className="case-row-measure col-span-3 grid gap-1.5 sm:col-span-1">
          <div className="flex items-center justify-between gap-2">
            <LightChip light={a.light} />
            <span className="tabular text-lg font-extrabold text-ink">{valueText(rule, a.value)}</span>
          </div>
          {rule && !a.manual && <Meter value={a.value} rule={a} light={a.light} />}
          <span className="text-right text-xs text-ink-3">{rule?.name}</span>
        </div>
      </button>
      {trailing}
      <ErpLink
        href={erp.object(a)}
        title={a.objectType === 'ticket' ? 'Abrir el ticket en Eleva' : 'Abrir la junta en Eleva'}
        className="shrink-0 rounded-lg p-2 hover:bg-card-2"
      />
    </li>
  );
}

/** Mesa priorizada, con búsqueda en sus casos y en las alertas de la capa de detalle. */
export function CaseTable() {
  const { alerts, filter, rules, board } = useBoardCtx();
  const scoring = board.scoring;
  const MAX_TABLE = scoring.table.max;
  const [showMore, setShowMore] = useState(false);
  const [adding, setAdding] = useState(false);
  const [query, setQuery] = useState('');
  const [resultLimit, setResultLimit] = useState(SEARCH_PAGE_SIZE);
  const searchHintId = useId();
  const searching = query.trim().length > 0;
  const { inside, outside } = useMemo(
    () => buildTable(alerts, filter, { scoring }),
    [alerts, filter, scoring],
  );
  const results = useMemo(() => {
    if (!query.trim()) return { inside: [], outside: [] };
    const match = makeMatcher(query);
    const hit = (a: BoardAlert) =>
      match(
        a.caseTitle,
        a.communityName,
        a.objectCode,
        a.affName,
        a.coordinatorName,
        a.ruleId,
        rules[a.ruleId]?.name,
        ...(a.related ?? []).flatMap((related) => [
          related.caseTitle,
          related.communityName,
          related.objectCode,
          related.affName,
          related.coordinatorName,
          related.ruleId,
          rules[related.ruleId]?.name,
        ]),
      );
    return { inside: inside.filter(hit), outside: outside.filter(hit) };
  }, [inside, outside, query, rules]);
  const resultCount = results.inside.length + results.outside.length;
  // Solo el número de casos en plazo (lo cuenta el servidor; los casos no se traen)
  const counts = board.inThreshold?.[filter.segment];
  const inPlazo = counts ? (filter.affId ? (counts.byAff[filter.affId] ?? 0) : counts.total) : 0;
  const reds = inside.filter((a) => a.light === 'rojo').length;
  const notBrought = inside.filter((a) => !a.broughtByTandem).length;

  return (
    <>
      <Panel
        className="case-table-panel"
        title="La mesa"
        description={`${inside.length} de ${MAX_TABLE} casos · ${reds} en rojo · ${notBrought} no traídos por el tándem`}
        action={
          <div className="case-table-actions flex flex-wrap items-center justify-end gap-3">
            <div className="flex gap-1.5">
              <Button size="sm" variant="secondary" onPress={() => setAdding(true)}>
                <Plus className="size-4" /> Añadir caso
              </Button>
              <CountChip icon={Presentation} value={inside.length} label="Casos en la mesa" tone="accent" />
              <CountChip
                icon={TriangleAlert}
                value={inside.length + outside.length}
                label="Casos fuera de umbral (mesa + capa de detalle)"
              />
              <CountChip
                icon={CircleCheck}
                value={inPlazo}
                label="Casos abiertos en plazo (dentro de umbral, no se muestran)"
                tone="ok"
              />
            </div>
            <div className="hidden gap-1 sm:flex" aria-hidden>
              {Array.from({ length: MAX_TABLE }, (_, i) => (
                <span
                  key={i}
                  className={`h-6 w-1.5 rounded-full ${
                    inside[i] ? (inside[i]!.light === 'rojo' ? 'bg-bad' : 'bg-warn') : 'bg-band'
                  }`}
                />
              ))}
            </div>
          </div>
        }
      >
        <div className="case-table-search-area">
          <SearchField
            className="case-table-search"
            aria-label="Buscar alertas"
            value={query}
            onChange={(value) => {
              setQuery(value);
              setResultLimit(SEARCH_PAGE_SIZE);
            }}
            variant="secondary"
          >
            <SearchField.Group>
              <SearchField.SearchIcon />
              <SearchField.Input
                placeholder="Caso, comunidad, persona o código…"
                aria-describedby={searchHintId}
              />
              <SearchField.ClearButton aria-label="Borrar búsqueda de alertas" />
            </SearchField.Group>
          </SearchField>
          <div className="case-table-search-summary">
            <p id={searchHintId}>
              Incluye alertas fuera de la mesa{' '}
              <span>· {filter.segment === 'AB' ? 'A + B' : 'Todos los segmentos'}</span>
            </p>
            <p role="status" aria-live="polite" aria-atomic="true">
              {searching &&
                `${resultCount} resultado${resultCount === 1 ? '' : 's'} · ${results.inside.length} en la mesa · ${results.outside.length} fuera`}
            </p>
          </div>
        </div>

        {searching ? (
          resultCount === 0 ? (
            <div className="case-table-search-empty">
              <SearchX size={23} strokeWidth={1.5} aria-hidden="true" />
              <h3>No hay alertas que coincidan</h3>
              <p>Prueba con otras palabras o revisa el filtro de segmento.</p>
            </div>
          ) : (
            <div className="case-table-search-results">
              {results.inside.length > 0 && (
                <section aria-labelledby={`${searchHintId}-inside`}>
                  <div className="case-table-result-heading">
                    <Presentation size={15} aria-hidden="true" />
                    <h3 id={`${searchHintId}-inside`}>En la mesa</h3>
                    <span>{results.inside.length}</span>
                  </div>
                  <ul className="divide-y divide-line" aria-label="Coincidencias en la mesa">
                    {results.inside.map((a) => (
                      <CaseRow key={a.key} a={a} />
                    ))}
                  </ul>
                </section>
              )}
              {results.outside.length > 0 && (
                <section aria-labelledby={`${searchHintId}-outside`}>
                  <div className="case-table-result-heading case-table-result-heading--outside">
                    <TriangleAlert size={15} aria-hidden="true" />
                    <h3 id={`${searchHintId}-outside`}>Fuera de la mesa</h3>
                    <span>{results.outside.length}</span>
                  </div>
                  <ul className="divide-y divide-line" aria-label="Coincidencias fuera de la mesa">
                    {results.outside.slice(0, resultLimit).map((a) => (
                      <CaseRow key={a.key} a={a} />
                    ))}
                  </ul>
                  {results.outside.length > resultLimit && (
                    <div className="case-table-search-more">
                      <span>
                        Mostrando {resultLimit} de {results.outside.length} alertas fuera de la mesa
                      </span>
                      <Button
                        size="sm"
                        variant="secondary"
                        onPress={() => setResultLimit((limit) => limit + SEARCH_PAGE_SIZE)}
                      >
                        Mostrar más alertas
                      </Button>
                    </div>
                  )}
                </section>
              )}
            </div>
          )
        ) : inside.length === 0 ? (
          <EmptyState>
            <EmptyState.Header>
              <EmptyState.Media variant="icon">
                <Inbox className="size-5" />
              </EmptyState.Media>
              <EmptyState.Title>Nada fuera de umbral</EmptyState.Title>
              <EmptyState.Description>
                Con este filtro no hay casos en ámbar ni en rojo.
              </EmptyState.Description>
            </EmptyState.Header>
          </EmptyState>
        ) : (
          <div className="grid gap-4">
            {BLOCKS.map((b) => {
              const list = inside.filter((a) => blockOfAlert(a) === b.id);
              // Casos del bloque que no entran en la mesa (están en la capa de detalle)
              const hidden = outside.filter((a) => blockOfAlert(a) === b.id).length;
              const enabled = Object.values(rules).some((r) => r.block === b.id && r.enabled);
              return (
                <div key={b.id} className="case-block">
                  <div className="case-block-heading mb-1 flex items-baseline gap-2 px-3">
                    <span className="text-xs font-bold tracking-wider text-orange-ink uppercase">
                      {b.name}
                    </span>
                    <span className="text-xs text-ink-3">
                      {scoring.blockMinutes[b.id]} min
                      {list.length > 0 && (
                        <>
                          {' '}
                          · {list.length} caso{list.length === 1 ? '' : 's'} · ~
                          {Math.round(scoring.blockMinutes[b.id] / list.length)} min/caso
                        </>
                      )}
                    </span>
                    {hidden > 0 && (
                      <span className="text-xs font-semibold text-orange-ink">
                        · +{hidden} en la capa de detalle
                      </span>
                    )}
                  </div>
                  {list.length ? (
                    <ul className="divide-y divide-line">
                      {list.map((a) => (
                        <CaseRow key={a.key} a={a} />
                      ))}
                    </ul>
                  ) : (
                    <p className="px-3 py-2 text-sm text-ink-3">
                      {!enabled
                        ? 'Sin datos todavía para este bloque.'
                        : hidden > 0
                          ? `Ninguno entra en la mesa: los ${hidden} casos de este bloque tienen menos prioridad que los de otros bloques (ver capa de detalle).`
                          : 'Nada fuera de umbral. Se pasa al siguiente.'}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {!searching && outside.length > 0 && (
          <div className="mt-4 border-t border-dashed border-line pt-3">
            <Button variant="ghost" size="sm" onPress={() => setShowMore((v) => !v)}>
              <ChevronDown className={`size-4 transition-transform ${showMore ? 'rotate-180' : ''}`} />
              {outside.length} alertas más · capa de detalle, no se proyecta
            </Button>
            {showMore && (
              <ul className="mt-2 divide-y divide-line opacity-80">
                {outside.slice(0, 200).map((a) => (
                  <CaseRow key={a.key} a={a} />
                ))}
              </ul>
            )}
            {showMore && outside.length > 200 && (
              <p className="px-3 pt-2 text-xs text-ink-3">Se muestran las 200 de mayor puntuación.</p>
            )}
          </div>
        )}
      </Panel>
      <ManualCaseSheet isOpen={adding} onClose={() => setAdding(false)} />
    </>
  );
}
